import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function AdminDashboard() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/posts/${p.id}`);
      toast.success("Deleted.");
      setPosts(posts.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 pt-16 md:pt-20" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-black/10 pb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-black/55 mb-3">
            Dashboard
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tighter leading-none">
            Your entries
          </h1>
        </div>
        <button
          onClick={() => navigate("/admin/new")}
          className="inline-flex items-center gap-3 bg-black text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-black/85 transition-colors self-start"
          data-testid="new-post-btn"
        >
          <Plus strokeWidth={1.5} className="w-4 h-4" />
          New entry
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-sm text-black/40 tracking-widest uppercase">
          Loading…
        </div>
      ) : posts.length === 0 ? (
        <div className="py-32 text-center">
          <p className="font-serif italic text-2xl text-black/60">
            No entries yet. Write the first one.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-black/10">
          {posts.map((p) => (
            <div
              key={p.id}
              className="py-6 grid grid-cols-1 md:grid-cols-12 items-center gap-4"
              data-testid={`admin-post-${p.id}`}
            >
              <div className="md:col-span-6">
                <h3 className="font-serif text-2xl font-normal leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-black/50 mt-1 line-clamp-1">{p.excerpt}</p>
              </div>
              <div className="md:col-span-2 text-[11px] uppercase tracking-[0.22em] text-black/55">
                {p.category}
              </div>
              <div className="md:col-span-2 text-[11px] uppercase tracking-[0.22em] text-black/50">
                {formatDate(p.created_at)} · {p.published ? "Public" : "Draft"}
              </div>
              <div className="md:col-span-2 flex items-center gap-2 justify-start md:justify-end">
                <Link
                  to={`/post/${p.slug}`}
                  className="p-2 border border-black/15 hover:border-black transition-colors"
                  title="View"
                  data-testid={`admin-view-${p.id}`}
                >
                  <Eye strokeWidth={1.25} className="w-4 h-4" />
                </Link>
                <Link
                  to={`/admin/edit/${p.id}`}
                  className="p-2 border border-black/15 hover:border-black transition-colors"
                  title="Edit"
                  data-testid={`admin-edit-${p.id}`}
                >
                  <Pencil strokeWidth={1.25} className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => onDelete(p)}
                  className="p-2 border border-black/15 hover:border-black hover:bg-black hover:text-white transition-colors"
                  title="Delete"
                  data-testid={`admin-delete-${p.id}`}
                >
                  <Trash2 strokeWidth={1.25} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
